import React, { ChangeEvent, FC, useCallback, useRef, useState } from 'react'
import { useConsumerSizes } from '../../../../../utils/responsive'
import { Button, Divider, Heading, Icon, Modal, Text } from '@afterpaytouch/core'
import { MAX_FILE_SIZE } from '../../../../../model'
import { Section75DisputeModalSteps, useSection75DisputeModalContext } from '../../Section75DisputeModalContext'
import { useTranslation } from 'next-i18next'

const I18N_NAMESPACE = ['dispute']

export const OtherDocs: FC = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { textSize } = useConsumerSizes()
  const { setStep, closeModal, files, setFiles } = useSection75DisputeModalContext()

  const proofOfPurchaseRef = useRef<HTMLInputElement>(null)
  const termsAndConditionsRef = useRef<HTMLInputElement>(null)
  const otherDocsRef = useRef<HTMLInputElement>(null)

  const [error, setError] = useState<string>('')
  const [proofOfPurchaseUploaded, setProofOfPurchaseUploaded] = useState<boolean>(false)
  const [termsAndConditionsUploaded, setTermsAndConditionsUploaded] = useState<boolean>(false)

  const backHandler = useCallback(() => {
    closeModal()
  }, [closeModal])

  const handleNext = useCallback(() => {
    // Validate required fields
    if (!proofOfPurchaseUploaded) {
      setError(t('dispute:section75DisputeModal:otherDocs:error:proofOfPurchaseRequired'))
      return
    }

    if (!termsAndConditionsUploaded) {
      setError(t('dispute:section75DisputeModal:otherDocs:error:termsAndConditionsRequired'))
      return
    }

    setStep(Section75DisputeModalSteps.Review)
  }, [setStep, proofOfPurchaseUploaded, termsAndConditionsUploaded])

  const createFileChangeHandler = useCallback(
    (fieldType: 'proof' | 'terms' | 'other') => (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(event.target.files != null ? event.target.files : [])

      const fileOutOfSizeList = fileList.filter((file: File) => file.size > MAX_FILE_SIZE)
      if (fileOutOfSizeList.length > 0) {
        setError(t('dispute:submitEvidence:error:exceedSize'))
        return
      }

      // Add files to global files array
      setFiles([...files, ...fileList])

      // Mark the specific field as uploaded
      if (fieldType === 'proof') {
        setProofOfPurchaseUploaded(true)
      } else if (fieldType === 'terms') {
        setTermsAndConditionsUploaded(true)
      }

      setError('')
      event.target.value = ''
    },
    [files, setFiles, t]
  )

  const proofOfPurchaseChangeHandler = useCallback(createFileChangeHandler('proof'), [createFileChangeHandler])

  const termsAndConditionsChangeHandler = useCallback(createFileChangeHandler('terms'), [createFileChangeHandler])

  const otherDocsChangeHandler = useCallback(createFileChangeHandler('other'), [createFileChangeHandler])

  const handleProofOfPurchaseClick = useCallback(() => {
    proofOfPurchaseRef.current?.click()
  }, [])

  const handleTermsAndConditionsClick = useCallback(() => {
    termsAndConditionsRef.current?.click()
  }, [])

  const handleOtherDocsClick = useCallback(() => {
    otherDocsRef.current?.click()
  }, [])

  return (
    <>
      <Modal.Header divider={true}>
        <div className='absolute z-10 pt-1'>
          <Button.Link size='Small' onClick={backHandler}>
            <Icon.CaretLeft size='Small' />
          </Button.Link>
        </div>
        <div className='text-center'>
          <Heading size='L'>{t('dispute:section75DisputeModal:otherDocs:heading')}</Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className='space-y-6'>
          {/* Proof of Purchase Section */}
          <div>
            <Text size='M' bold className='mb-2'>
              {t('dispute:section75DisputeModal:otherDocs:proofOfPurchase')}
            </Text>
            <Text size={textSize} className='mb-4 text-gray-600'>
              {t('dispute:section75DisputeModal:otherDocs:proofOfPurchaseDesc')}
            </Text>
            <Button kind='Ghost' padding='Fluid' size='Large' onClick={handleProofOfPurchaseClick}>
              {t('dispute:section75DisputeModal:otherDocs:chooseFile')}
            </Button>
            <input type='file' className='hidden' onChange={proofOfPurchaseChangeHandler} accept='image/*,.pdf,.doc,.docx' ref={proofOfPurchaseRef} />
          </div>

          {/* Dotted Divider */}
          <Divider kind='Dashed' />

          {/* Terms and Conditions Section */}
          <div>
            <Text size='M' bold className='mb-2'>
              {t('dispute:section75DisputeModal:otherDocs:termsAndConditions')}
            </Text>
            <Text size={textSize} className='mb-4 text-gray-600'>
              {t('dispute:section75DisputeModal:otherDocs:termsAndConditionsDesc')}
            </Text>
            <Button kind='Ghost' padding='Fluid' size='Large' onClick={handleTermsAndConditionsClick}>
              {t('dispute:section75DisputeModal:otherDocs:chooseFile')}
            </Button>
            <input type='file' className='hidden' onChange={termsAndConditionsChangeHandler} accept='image/*,.pdf,.doc,.docx' ref={termsAndConditionsRef} />
          </div>

          {/* Dotted Divider */}
          <Divider kind='Dashed' />

          {/* Other Documents Section */}
          <div>
            <Text size='M' bold className='mb-2'>
              {t('dispute:section75DisputeModal:otherDocs:otherDocuments')}
            </Text>
            <Text size={textSize} className='mb-2 text-gray-600'>
              {t('dispute:section75DisputeModal:otherDocs:otherDocumentsDesc')}
            </Text>
            <ul className='mb-4 ml-4 space-y-1'>
              <li className='flex items-start'>
                <span className='mr-2 text-gray-600'>•</span>
                <Text size={textSize} className='text-gray-600'>
                  {t('dispute:section75DisputeModal:otherDocs:correspondenceWithMerchant')}
                </Text>
              </li>
              <li className='flex items-start'>
                <span className='mr-2 text-gray-600'>•</span>
                <Text size={textSize} className='text-gray-600'>
                  {t('dispute:section75DisputeModal:otherDocs:screenshots')}
                </Text>
              </li>
            </ul>
            <Button kind='Ghost' padding='Fluid' size='Large' onClick={handleOtherDocsClick}>
              {t('dispute:section75DisputeModal:otherDocs:chooseFiles')}
            </Button>
            <input type='file' className='hidden' multiple onChange={otherDocsChangeHandler} accept='image/*,.pdf,.doc,.docx' ref={otherDocsRef} />
          </div>

          {/* Display all uploaded files */}
          {files.length > 0 && (
            <div>
              <Text className='text-gray-600'>{t('dispute:section75DisputeModal:otherDocs:uploadedFiles')}</Text>
              <ul className='mt-1'>
                {files.map((file, index) => (
                  <li key={index} className='text-sm'>
                    <Text className='text-gray-600'>{file.name}</Text>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className='mt-4'>
              <Text className='text-red-600'>{error}</Text>
            </div>
          )}
        </div>

        <div className='mt-6'>
          <Button padding='Fluid' size='Large' onClick={handleNext}>
            {t('dispute:section75DisputeModal:otherDocs:next')}
          </Button>
        </div>
      </Modal.Content>
    </>
  )
}
